import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Zap, Package, Truck, CreditCard, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PhoneAuthModal from '../components/PhoneAuthModal';
import AlertModal from '../components/AlertModal';
import { orderAPI } from '../utils/api';

function OrderHistoryPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'info', message: '' });
  const navigate = useNavigate();

  const showAlert = (type, message) => {
    setAlertModal({ isOpen: true, type, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', message: '' });
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setShowAuthModal(true);
        setLoading(false);
        return;
      }

      const data = await orderAPI.getOrders();
      setOrders(data);
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      PENDING: '결제 대기',
      PAID: '결제 완료',
      PROCESSING: '배송 준비 중',
      SHIPPED: '배송 중',
      DELIVERED: '배송 완료',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      PENDING: 'text-yellow-700 bg-yellow-50 border-yellow-200',
      PAID: 'text-teal-700 bg-teal-50 border-teal-200',
      PROCESSING: 'text-blue-700 bg-blue-50 border-blue-200',
      SHIPPED: 'text-purple-700 bg-purple-50 border-purple-200',
      DELIVERED: 'text-gray-700 bg-gray-50 border-gray-200',
    };
    return colorMap[status] || 'text-gray-700 bg-gray-50 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">주문 내역</h1>
          <div className="space-y-4 max-w-4xl mx-auto">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-24" />
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full w-20" />
                </div>
                <div className="h-5 bg-gray-200 rounded w-48 mb-3" />
                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                  <div className="h-4 bg-gray-200 rounded w-16" />
                  <div className="h-6 bg-gray-200 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">주문 내역</h1>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="text-gray-400 mb-4">
              <Package className="w-16 h-16 mx-auto" />
            </div>
            <p className="text-lg text-gray-600 mb-2">주문 내역이 없습니다.</p>
            <p className="text-sm text-gray-400 mb-6">첫 주문을 해보세요!</p>
            <button
              onClick={() => navigate('/products')}
              className="bg-teal-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors"
            >
              쇼핑 시작하기
            </button>
          </div>
        ) : (
          <div className="space-y-4 max-w-4xl mx-auto">
            {orders.map((order) => (
              <div
                key={order.uuid}
                className="bg-white rounded-xl border border-gray-200 p-6 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      주문번호: {order.uuid.substring(0, 8)}...
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`}>
                    {getStatusText(order.status)}
                  </span>
                </div>

                <div className="mb-3">
                  {order.items && order.items.length > 0 ? (
                    <p className="text-gray-900 font-medium">
                      {order.items[0].product.name}
                      {order.items.length > 1 && ` 외 ${order.items.length - 1}개`}
                    </p>
                  ) : (
                    <p className="text-gray-400 text-sm">상품 정보 없음</p>
                  )}
                </div>

                {order.has_fast_shipping && (
                  <div className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 px-2 py-1 rounded text-sm mb-3">
                    <Zap className="w-3 h-3" />
                    빠른 배송
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                  <span className="text-gray-500">결제 금액</span>
                  <span className="text-xl font-bold text-teal-600">
                    {order.amount?.toLocaleString() || 0}원
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />

      {/* 주문 상세 모달 */}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">주문 상세</h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 주문 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-teal-500" />
                  <h3 className="font-semibold text-gray-900">주문 정보</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">주문번호</span>
                    <span className="font-mono text-gray-900">{selectedOrder.uuid}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">주문일시</span>
                    <span className="text-gray-900">{new Date(selectedOrder.created_at).toLocaleString('ko-KR')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">상태</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedOrder.status)}`}>
                      {getStatusText(selectedOrder.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* 주문 상품 목록 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">주문 상품</h3>
                <div className="space-y-3">
                  {selectedOrder.items && selectedOrder.items.map((item, idx) => {
                    const itemPrice = item.option_price != null ? item.option_price : item.product.price;
                    return (
                      <div key={idx} className="flex gap-4 bg-gray-50 rounded-lg p-4">
                        {item.product.image_urls && item.product.image_urls[0] && (
                          <img
                            src={item.product.image_urls[0]}
                            alt={item.product.name}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 mb-1">{item.product.name}</p>
                          {item.option_name && (
                            <span className="inline-flex items-center text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded mb-1">
                              옵션: {item.option_name}
                            </span>
                          )}
                          <p className="text-sm text-gray-500 mb-2">
                            {itemPrice?.toLocaleString()}원 × {item.quantity}개
                          </p>
                          {item.has_fast_shipping && (
                            <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 px-2 py-1 rounded text-xs">
                              <Zap className="w-3 h-3" />
                              빠른 배송
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-teal-600">
                            {(itemPrice * item.quantity)?.toLocaleString()}원
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 결제 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-teal-500" />
                  <h3 className="font-semibold text-gray-900">결제 정보</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">상품 금액</span>
                    <span className="text-gray-900">{selectedOrder.subtotal?.toLocaleString()}원</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">배송비</span>
                    <span className="text-gray-900">{selectedOrder.shipping_fee?.toLocaleString()}원</span>
                  </div>
                  {selectedOrder.discount > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>할인</span>
                      <span>-{selectedOrder.discount?.toLocaleString()}원</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-gray-200 text-base font-bold">
                    <span className="text-gray-900">총 결제 금액</span>
                    <span className="text-teal-600">{selectedOrder.amount?.toLocaleString()}원</span>
                  </div>
                </div>
              </div>

              {/* 배송 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Truck className="w-5 h-5 text-teal-500" />
                  <h3 className="font-semibold text-gray-900">배송 정보</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">수령인</span>
                    <span className="text-gray-900">{selectedOrder.address?.recipient}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">연락처</span>
                    <span className="text-gray-900">{selectedOrder.address?.phone}</span>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">주소</p>
                    <p className="text-gray-900">
                      ({selectedOrder.address?.postal_code}) {selectedOrder.address?.address}
                    </p>
                    <p className="text-gray-700">
                      {selectedOrder.address?.detail_address}
                    </p>
                  </div>
                  {selectedOrder.address?.request && (
                    <div>
                      <p className="text-gray-500 mb-1">배송 요청사항</p>
                      <p className="text-gray-900">{selectedOrder.address.request}</p>
                    </div>
                  )}
                  {selectedOrder.tracking_no && (
                    <>
                      <div className="flex justify-between pt-3 border-t border-gray-200">
                        <span className="text-gray-500">택배사</span>
                        <span className="text-gray-900">{selectedOrder.courier_name || '미지정'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">운송장번호</span>
                        <span className="font-mono text-gray-900">{selectedOrder.tracking_no}</span>
                      </div>
                    </>
                  )}
                  {selectedOrder.fmsSlipNo && (
                    <div className="flex justify-between pt-3 border-t border-gray-200">
                      <span className="text-gray-500">출고번호</span>
                      <span className="font-mono text-gray-900">{selectedOrder.fmsSlipNo}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <PhoneAuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          navigate('/');
        }}
        onSuccess={() => {
          setShowAuthModal(false);
          loadOrders();
        }}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        message={alertModal.message}
      />
    </div>
  );
}

export default OrderHistoryPage;
